import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Mountain, Eye, Camera, Volume2, VolumeX } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { MagneticButton } from "../components/MagneticButton";
import { Counter } from "../components/Counter";
import { useT, usePageMeta } from "../lib/i18n";

/*
 *  ABOUT PAGE — Romain Bruneau
 *  ─────────────────────────────────────────────────────────────────
 *  A premium, editorial single-page story:
 *
 *    1. Hero            — fullscreen portrait + headline + CTA
 *    2. Story           — 5-paragraph editorial reflection
 *    3. Vision pillars  — 3 cards (Comprendre · Anticiper · Capturer)
 *    4. Palmarès        — 3 disciplines with animated medal counters
 *    5. Gallery preview — 6-photo masonry with lightbox
 *    6. Showreel        — vertical cinema with sound toggle
 *    7. For brands      — who I work with (chips + intro)
 *    8. Final CTA       — large headline + magnetic button
 */

/* ─────────── 1. HERO — editorial magazine spread ───────────────
   Distinct from the Home hero (which is fullscreen + bottom text).
   Here the photo lives in a framed portrait card on the left while
   the right column carries vertical-stacked typography, an issue
   number and a refined press-release feel. */
const AboutHero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto   = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yCopy    = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      data-testid="about-hero"
      className="relative min-h-screen w-full bg-ink text-bone overflow-hidden grain"
    >
      {/* Soft warm radial accents — editorial paper-stock feel */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 30%, rgba(184,169,154,0.10) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 90% 80%, rgba(255,170,90,0.06) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      {/* Top issue strip — magazine masthead */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 1 }}
        className="absolute top-0 left-0 right-0 z-10 pt-28 md:pt-32"
      >
        <div className="container-x flex items-center justify-between text-[10px] tracking-[0.32em] uppercase text-bone/55">
          <div className="flex items-center gap-4">
            <span className="font-display text-base tracking-[0.32em] text-stone">N° 01</span>
            <span className="h-px w-10 bg-stone/40" />
            <span>Profile · Volume I</span>
          </div>
          <span className="hidden md:inline">Pau · France · Est. 2014</span>
        </div>
      </motion.div>

      <div className="container-x relative pt-44 md:pt-48 pb-24 md:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center min-h-screen">
        {/* LEFT — framed portrait card */}
        <motion.div
          style={{ y: yPhoto }}
          className="lg:col-span-6 order-1 relative"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-[500px] mx-auto lg:mx-0"
          >
            {/* Stone accent block — sits diagonally behind the photo */}
            <div
              aria-hidden="true"
              className="absolute -inset-4 md:-top-6 md:-left-6 md:-right-2 md:-bottom-2 -z-10"
              style={{ background: "linear-gradient(135deg, #B8A99A 0%, #8B7D6B 100%)" }}
            />
            {/* Photo with cream paper frame */}
            <div
              className="relative aspect-[4/5] overflow-hidden shadow-[0_50px_80px_-20px_rgba(0,0,0,0.7)]"
              style={{ background: "#F2EDE0", padding: "14px 14px 56px 14px" }}
            >
              <img
                src="/photos/about_01.jpg"
                alt="Romain Bruneau · sur le terrain"
                className="w-full h-full object-cover"
              />
              {/* Hand-written-feel caption inside the paper frame */}
              <div className="absolute bottom-0 left-0 right-0 h-14 flex items-center justify-between px-4 text-ink">
                <span className="font-display italic text-sm">Romain Bruneau · Sommet</span>
                <span className="text-[9px] tracking-[0.28em] uppercase opacity-50">
                  Mont Blanc · 2023
                </span>
              </div>
            </div>
            {/* Subtle photographer's stamp top-right */}
            <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-ink ring-1 ring-stone/40 flex items-center justify-center rotate-12">
              <span className="font-display italic text-bone text-sm">RB</span>
            </div>
          </motion.div>
        </motion.div>

        {/* RIGHT — editorial typography */}
        <motion.div
          style={{ y: yCopy }}
          className="lg:col-span-6 order-2 relative"
        >
          <FadeIn delay={1.0}>
            <div className="flex items-center gap-3 mb-6 text-bone/80">
              <span className="h-px w-10 bg-stone/60" />
              <div className="eyebrow text-stone">{t("about2.hero.eyebrow")}</div>
            </div>
          </FadeIn>

          {/* Stacked name — completely different rhythm from Home */}
          <div data-testid="about-hero-title" className="overflow-hidden">
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.88] tracking-[-0.03em] text-bone"
            >
              {t("about2.hero.titleA")}
            </motion.div>
          </div>
          <div className="overflow-hidden -mt-1">
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display italic font-light text-5xl md:text-6xl lg:text-[7rem] leading-[0.88] tracking-[-0.03em] text-stone"
            >
              {t("about2.hero.titleB")}
            </motion.div>
          </div>

          {/* Animated accent */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.85, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-px w-32 bg-stone/70 mt-7 origin-left"
          />

          <FadeIn delay={1.7}>
            <div className="mt-5 text-[11px] tracking-[0.32em] uppercase text-stone">
              {t("about2.hero.role")}
            </div>
          </FadeIn>

          <FadeIn delay={1.9}>
            <div className="mt-5 flex flex-wrap gap-2">
              {["about2.hero.tag1", "about2.hero.tag2", "about2.hero.tag3"].map((k) => (
                <span
                  key={k}
                  className="px-3 py-1 rounded-full ring-1 ring-bone/20 backdrop-blur-sm text-[10px] tracking-[0.22em] uppercase text-bone/75"
                >
                  {t(k)}
                </span>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={2.1}>
            <p className="mt-8 max-w-md text-bone/75 text-base leading-relaxed border-l-2 border-stone/40 pl-5 italic">
              {t("about2.hero.lead")}
            </p>
          </FadeIn>

          <FadeIn delay={2.3}>
            <div className="mt-10">
              <a
                href="#story"
                data-testid="about-hero-cta"
                onClick={(e) => {
                  e.preventDefault();
                  const target = document.getElementById("story");
                  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-block"
              >
                <MagneticButton className="btn-line" as="span">
                  <span>{t("about2.hero.cta")}</span>
                  <ArrowRight size={14} />
                </MagneticButton>
              </a>
            </div>
          </FadeIn>
        </motion.div>
      </div>

      {/* Bottom strip — page footer that reinforces magazine feel */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1 }}
        className="absolute bottom-6 md:bottom-8 left-0 right-0 z-10"
      >
        <div className="container-x flex items-center justify-between text-[10px] tracking-[0.32em] uppercase text-bone/40">
          <span>romainbruneau.com</span>
          <span className="flex items-center gap-2 text-bone/55">
            <ArrowDown size={12} className="animate-bounce" />
            <span>{t("about2.hero.scroll")}</span>
          </span>
          <span className="hidden md:inline">Profile · 2026</span>
        </div>
      </motion.div>
    </section>
  );
};

/* ─────────── 3. VISION PILLARS ────────────────────────────────── */
const PILLARS = [
  { key: "p1", Icon: Mountain, photo: "/photos/about_06.jpg" },
  { key: "p2", Icon: Eye,      photo: "/photos/photo_85.jpg" },
  { key: "p3", Icon: Camera,   photo: "/photos/about_04.jpg" },
];

const VisionPillars = ({ t }) => (
  <section id="story" data-testid="about-vision" className="bg-ink py-20 md:py-32 border-y border-bone/8 relative scroll-mt-24">
    <div className="container-x">
      <FadeIn>
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-10 bg-stone/50" />
            <div className="eyebrow text-stone">{t("about2.vision.eyebrow")}</div>
            <span className="h-px w-10 bg-stone/50" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-bone leading-[0.95] mb-5">
            {t("about2.vision.title")}{" "}
            <span className="italic text-stone">{t("about2.vision.titleIt")}</span>.
          </h2>
          <p className="text-bone/60 text-sm md:text-base max-w-xl mx-auto">
            {t("about2.vision.intro")}
          </p>
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {PILLARS.map((p, i) => {
          const Icon = p.Icon;
          return (
            <FadeIn key={p.key} delay={i * 0.1}>
              <article
                data-testid={`pillar-${p.key}`}
                className="group relative aspect-[4/5] overflow-hidden bg-black rounded-sm"
              >
                <img
                  src={p.photo}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-50 transition-all duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-80 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/20" />
                <div className="relative h-full flex flex-col justify-between p-7 md:p-9 text-bone">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-sm tracking-[0.3em] text-bone/70">
                      0{i + 1}
                    </span>
                    <span className="w-11 h-11 rounded-full ring-1 ring-bone/20 flex items-center justify-center group-hover:ring-stone/50 transition-all">
                      <Icon size={16} className="text-stone" />
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl italic leading-tight mb-3">
                      {t(`about2.vision.${p.key}.title`)}
                    </h3>
                    <div className="h-px w-10 bg-stone/60 mb-4 transition-all duration-700 group-hover:w-20" />
                    <p className="text-sm text-bone/70 leading-relaxed">
                      {t(`about2.vision.${p.key}.text`)}
                    </p>
                  </div>
                </div>
              </article>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

/* ─────────── 4. PALMARÈS ──────────────────────────────────────── */
const DISCIPLINES = [
  { key: "c1", n: "01", count: 4,    countLabel: "intl.",    lines: 5, photo: "/photos/photo_05.jpg" },
  { key: "c2", n: "02", count: 43,   countLabel: "mondial",  lines: 7, photo: "/photos/about_02.jpg" },
  { key: "c3", n: "03", count: 4807, countLabel: "m",        lines: 5, photo: "/photos/photo_36.jpg" },
];

const Palmares = ({ t }) => (
  <section
    data-testid="about-palmares"
    className="bg-ink py-20 md:py-32 relative overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none opacity-40"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, rgba(184,169,154,0.10) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 80% 90%, rgba(255,170,90,0.07) 0%, rgba(0,0,0,0) 60%)",
      }}
    />
    <div className="container-x relative">
      <FadeIn>
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-stone/60" />
              <div className="eyebrow text-stone">{t("about2.palm.eyebrow")}</div>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl text-bone leading-[0.95]">
              {t("about2.palm.title")}{" "}
              <span className="italic text-stone">{t("about2.palm.titleIt")}</span>.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-bone/65 text-sm md:text-base leading-relaxed max-w-md italic">
              {t("about2.palm.intro")}
            </p>
          </div>
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {DISCIPLINES.map((d, idx) => (
          <FadeIn key={d.key} delay={idx * 0.12}>
            <div
              data-testid={`palm-${d.key}`}
              className="relative bg-[#111114] rounded-sm overflow-hidden ring-1 ring-bone/8 hover:ring-stone/30 transition-all duration-500 group"
            >
              {/* Top photo */}
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={d.photo}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-[1.05] transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111114] via-[#111114]/30 to-transparent" />
                {/* Discipline number */}
                <div className="absolute top-5 left-5 font-display text-sm tracking-[0.32em] text-bone/80">
                  {d.n}
                </div>
                {/* Animated headline counter */}
                <div className="absolute bottom-5 left-5 right-5 text-bone">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-5xl md:text-6xl tracking-[-0.03em] leading-none">
                      <Counter to={d.count} />
                    </span>
                    <span className="text-[10px] tracking-[0.28em] uppercase text-stone">
                      {d.countLabel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Discipline label */}
              <div className="p-7 md:p-8">
                <h3 className="font-display text-2xl md:text-3xl italic text-bone leading-tight mb-5">
                  {t(`about2.palm.${d.key}.label`)}
                </h3>
                <ul className="space-y-3">
                  {Array.from({ length: d.lines }).map((_, i) => {
                    const text = t(`about2.palm.${d.key}.${i}`);
                    if (!text || text.startsWith("about2.palm.")) return null;
                    return (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-sm text-bone/75 leading-relaxed group/li"
                      >
                        <span className="mt-2 w-1 h-1 rounded-full bg-stone shrink-0 group-hover/li:bg-bone transition-colors" />
                        <span>{text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────── 6. SHOWREEL ──────────────────────────────────────── */
const AboutShowreel = ({ t }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    if (!videoRef.current) return;
    const next = !muted;
    videoRef.current.muted = next;
    setMuted(next);
    if (!next) videoRef.current.play().catch(() => {});
  };

  return (
    <section data-testid="about-showreel" className="relative bg-ink py-20 md:py-32 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, rgba(255,170,90,0.10) 0%, rgba(0,0,0,0) 55%), linear-gradient(180deg, #0a0a0a 0%, #050505 100%)",
        }}
      />
      <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Copy */}
        <FadeIn className="lg:col-span-5 order-2 lg:order-1 text-bone">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-stone/60" />
            <div className="eyebrow text-stone">{t("about2.reel.eyebrow")}</div>
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95]">
            {t("about2.reel.title")}
            <br />
            <span className="italic text-stone">{t("about2.reel.titleIt")}</span>
          </h2>
          <p className="mt-6 text-bone/65 leading-relaxed max-w-md">
            {t("about2.reel.intro")}
          </p>
          <button
            onClick={toggleSound}
            data-testid="reel-sound-toggle"
            className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full ring-1 ring-bone/20 hover:ring-stone/60 hover:-translate-y-0.5 transition-all text-[11px] tracking-[0.22em] uppercase text-bone/85 hover:text-bone"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            <span>{muted ? t("about2.showreel.enableSound") : t("about2.showreel.muteSound")}</span>
          </button>
        </FadeIn>

        {/* Vertical cinema clip */}
        <FadeIn delay={0.15} className="lg:col-span-7 order-1 lg:order-2 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            style={{
              width: "min(78vw, 380px)",
              aspectRatio: "9 / 16",
            }}
          >
            {/* Warm halo */}
            <div
              aria-hidden="true"
              className="absolute -inset-8 pointer-events-none opacity-60 blur-3xl rounded-full"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(255,170,90,0.22) 0%, rgba(0,0,0,0) 65%)",
              }}
            />
            {/* Cinema frame with film-strip perforations */}
            <div className="absolute inset-0 rounded-md overflow-hidden ring-1 ring-bone/15 shadow-[0_50px_80px_-25px_rgba(0,0,0,0.85)] bg-black">
              <video
                ref={videoRef}
                src="/videos/showreel-about.mp4"
                poster="/videos/showreel-about-poster.jpg"
                autoPlay
                muted={muted}
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="about-reel-video"
              />
              {/* Subtle screen glare */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-35"
                style={{
                  background:
                    "linear-gradient(115deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 75%, rgba(255,255,255,0.08) 100%)",
                }}
              />
              {/* Film-strip perforations */}
              <div className="absolute top-3 left-1.5 right-1.5 flex justify-between pointer-events-none">
                {Array.from({ length: 10 }).map((_, i) => (
                  <span key={i} className="w-1 h-2 rounded-sm bg-bone/25" />
                ))}
              </div>
              <div className="absolute bottom-3 left-1.5 right-1.5 flex justify-between pointer-events-none">
                {Array.from({ length: 10 }).map((_, i) => (
                  <span key={i} className="w-1 h-2 rounded-sm bg-bone/25" />
                ))}
              </div>
            </div>
          </motion.div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─────────── 7. FOR BRANDS ────────────────────────────────────── */
const ForBrands = ({ t }) => (
  <section data-testid="about-brands" className="bg-ink py-20 md:py-32 border-t border-bone/8">
    <div className="container-x">
      <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
        <FadeIn className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-stone/60" />
            <div className="eyebrow text-stone">{t("about2.brands.eyebrow")}</div>
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-bone leading-[0.95]">
            {t("about2.brands.title")}
            <br />
            <span className="italic text-stone">{t("about2.brands.titleIt")}</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.15} className="md:col-span-7">
          <p className="text-bone/75 leading-relaxed text-base md:text-lg max-w-2xl">
            {t("about2.brands.intro")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <span
                key={i}
                data-testid={`brand-tag-${i}`}
                className="px-5 py-2.5 rounded-full ring-1 ring-bone/15 text-bone/80 text-[11px] tracking-[0.22em] uppercase hover:ring-stone/60 hover:text-bone hover:-translate-y-0.5 transition-all cursor-default"
              >
                {t(`about2.brands.tag.${i}`)}
              </span>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ─────────── 8. FINAL CTA ─────────────────────────────────────── */
const AboutCta = ({ t }) => (
  <section data-testid="about-cta" className="relative bg-ink py-24 md:py-32 overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse at 50% 50%, rgba(255,170,90,0.12) 0%, rgba(0,0,0,0) 55%)",
      }}
    />
    <div className="container-x relative text-center">
      <FadeIn>
        <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.95] text-bone max-w-4xl mx-auto mb-6">
          {t("about2.cta.title")}
        </h2>
        <p className="font-display italic text-2xl md:text-3xl text-stone mb-12">
          {t("about2.cta.sub")}
        </p>
        <Link to="/contact" data-testid="about-cta-btn">
          <MagneticButton className="btn-line" as="span">
            <span>{t("about2.cta.btn")}</span>
            <ArrowRight size={14} />
          </MagneticButton>
        </Link>
      </FadeIn>
    </div>
  </section>
);

/* ─────────── PAGE ENTRY ───────────────────────────────────────── */
export default function About() {
  const t = useT();
  usePageMeta({ titleKey: "meta.about.title", descKey: "meta.about.desc", path: "/about" });

  return (
    <main data-testid="about-page" className="bg-ink text-bone">
      <AboutHero t={t} />
      <VisionPillars t={t} />
      <Palmares t={t} />
      <AboutShowreel t={t} />
      <ForBrands t={t} />
      <AboutCta t={t} />
    </main>
  );
}
